import { CommonModule } from '@angular/common';
import { Component, ElementRef, AfterViewInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';

import { ArtifactService } from '../../../../core/artifact/artifact.service';

import { NavBaseComponent } from './nav-base';
import { persistStepCapture, uploadCapture } from './nav-capture-storage';

/**
 * Touch-enabled canvas signature pad.
 *
 * - Draws with finger or mouse on a canvas element
 * - Clear button resets the canvas
 * - Emits `signed` when the crew lead confirms a non-empty signature
 * - Captures the signature as a base64 PNG data URL
 * - When `required` is true, signals readiness to the step gating system
 *   by calling node.props.onReady(true/false)
 */
@Component({
    selector: 'app-nav-customer-signature',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        .sig-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
            overflow: hidden;
        }
        .sig-header {
            padding: 0.85rem 1.1rem 0;
            display: flex; align-items: center; justify-content: space-between;
        }
        .sig-title {
            font-size: 0.8rem; font-weight: 700; color: #888;
            text-transform: uppercase; letter-spacing: 0.05em;
        }
        .sig-actions { display: flex; gap: 0.5rem; }
        .sig-btn {
            padding: 0.3rem 0.75rem; border-radius: 6px;
            font-size: 0.82rem; font-weight: 600; cursor: pointer;
            border: 1.5px solid #e0e0e0; background: white; color: #555;
            font-family: inherit;
        }
        .sig-btn:active { background: #f0f0f0; }
        .sig-btn.confirm { background: #2d6a2d; color: white; border-color: #2d6a2d; }
        .sig-btn.confirm:active { background: #1f4d1f; }
        .sig-btn.confirm:disabled { background: #b0b8b0; border-color: #b0b8b0; cursor: not-allowed; }

        .canvas-wrap {
            margin: 0.6rem 1.1rem 0;
            border: 2px dashed #ced4da; border-radius: 8px;
            background: #fafafa; cursor: crosshair;
            touch-action: none; /* prevent scroll while drawing */
        }
        .canvas-wrap.has-sig { border-color: #2d6a2d; border-style: solid; }
        canvas { display: block; width: 100%; }

        .sig-hint {
            text-align: center; font-size: 0.8rem; color: #bbb;
            padding: 0.5rem 0 0.85rem;
        }

        /* Signed confirmation */
        .sig-upload-warning {
            display: flex; align-items: center; gap: 0.5rem;
            margin-top: 0.5rem; padding: 0.55rem 0.7rem;
            background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;
            font-size: 0.82rem; color: #b91c1c; line-height: 1.3;
        }
        .sig-upload-warning span { flex: 1; min-width: 0; }
        .sig-upload-warning.uploading {
            background: #f5f7fa; border-color: #e0e0e0; color: #666;
        }
        .sig-confirmed {
            margin: 0 1.1rem 0.85rem;
            background: #f0f7f0; border: 1px solid #c6e2c6;
            border-radius: 8px; padding: 0.75rem 1rem;
            display: flex; align-items: center; gap: 0.6rem;
            font-size: 0.9rem; color: #2d6a2d; font-weight: 600;
        }
    `],
    template: `
        <div class="sig-card" *ngIf="node.props?.enabled !== false">

            <!-- Confirmed state -->
            <div class="sig-confirmed" *ngIf="confirmed">
                <i class="pi pi-check-circle" style="font-size:1.2rem;"></i>
                Signed{{ signedAt ? ' at ' + signedAt : '' }}
                <button class="sig-btn" style="margin-left:auto;" (click)="reset()">Re-sign</button>
            </div>

            <!-- Storage failure. Non-blocking by design — a crew with no signal must still be able to
                 finish the move — but it has to be visible and actionable, which it was not: the
                 confirmed state showed a green tick whether or not the signature had been stored. -->
            <div class="sig-upload-warning" *ngIf="confirmed && uploadState === 'failed'">
                <i class="pi pi-exclamation-triangle"></i>
                <span>Signature not saved yet. You can continue, but it will be lost.</span>
                <button class="sig-btn" (click)="retryStore()">Retry</button>
            </div>

            <div class="sig-upload-warning uploading" *ngIf="confirmed && uploadState === 'uploading'">
                <i class="pi pi-spin pi-spinner"></i>
                <span>Saving signature…</span>
            </div>

            <!-- Signing state -->
            <ng-container *ngIf="!confirmed">
                <div class="sig-header">
                    <span class="sig-title">{{ node.props?.title || 'Signature Required' }}</span>
                    <div class="sig-actions">
                        <button class="sig-btn" (click)="clear()" [disabled]="!hasSig">Clear</button>
                        <button class="sig-btn confirm" (click)="confirm()" [disabled]="!hasSig">
                            <i class="pi pi-check"></i> Done
                        </button>
                    </div>
                </div>

                <div class="canvas-wrap" [class.has-sig]="hasSig">
                    <canvas #sigCanvas
                        [width]="canvasWidth" [height]="140"
                        (mousedown)="startDraw($event)"
                        (mousemove)="draw($event)"
                        (mouseup)="endDraw()"
                        (mouseleave)="endDraw()"
                        (touchstart)="startDrawTouch($event)"
                        (touchmove)="drawTouch($event)"
                        (touchend)="endDraw()">
                    </canvas>
                </div>
                <div class="sig-hint" *ngIf="!hasSig">Sign here with your finger or mouse</div>
            </ng-container>
        </div>
    `,
})
export class NavCustomerSignatureComponent extends NavBaseComponent implements AfterViewInit {
    @ViewChild('sigCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    hasSig = false;
    confirmed = false;
    signedAt = '';
    canvasWidth = 360;

    private ctx!: CanvasRenderingContext2D;
    private drawing = false;
    private lastX = 0;
    private lastY = 0;

    ngAfterViewInit() {
        if (!this.canvasRef) return;
        const canvas = this.canvasRef.nativeElement;
        // Fit canvas to its display width
        const rect = canvas.getBoundingClientRect();
        if (rect.width > 0) {
            this.canvasWidth = Math.round(rect.width);
            canvas.width = this.canvasWidth;
        }
        this.ctx = canvas.getContext('2d')!;
        this.ctx.strokeStyle = '#1a1a1a';
        this.ctx.lineWidth = 2.5;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
    }

    private coords(e: MouseEvent): [number, number] {
        const rect = this.canvasRef.nativeElement.getBoundingClientRect();
        const scaleX = this.canvasRef.nativeElement.width / rect.width;
        const scaleY = this.canvasRef.nativeElement.height / rect.height;
        return [(e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY];
    }

    private touchCoords(e: TouchEvent): [number, number] {
        const rect = this.canvasRef.nativeElement.getBoundingClientRect();
        const t = e.touches[0];
        const scaleX = this.canvasRef.nativeElement.width / rect.width;
        const scaleY = this.canvasRef.nativeElement.height / rect.height;
        return [(t.clientX - rect.left) * scaleX, (t.clientY - rect.top) * scaleY];
    }

    startDraw(e: MouseEvent) {
        this.drawing = true;
        [this.lastX, this.lastY] = this.coords(e);
        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
    }

    draw(e: MouseEvent) {
        if (!this.drawing) return;
        const [x, y] = this.coords(e);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
        [this.lastX, this.lastY] = [x, y];
        this.hasSig = true;
        this._notifyReady(false);
    }

    startDrawTouch(e: TouchEvent) {
        e.preventDefault();
        this.drawing = true;
        [this.lastX, this.lastY] = this.touchCoords(e);
        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
    }

    drawTouch(e: TouchEvent) {
        e.preventDefault();
        if (!this.drawing) return;
        const [x, y] = this.touchCoords(e);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
        [this.lastX, this.lastY] = [x, y];
        this.hasSig = true;
        this._notifyReady(false);
    }

    endDraw() { this.drawing = false; }

    clear() {
        const canvas = this.canvasRef.nativeElement;
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.hasSig = false;
        this._notifyReady(false);
    }

    confirm() {
        if (!this.hasSig) return;
        this.confirmed = true;
        this.signedAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Held, not re-read. `*ngIf="!confirmed"` destroys the canvas the moment this runs, so a
        // later retry has nothing to read back — `retryStore()` used to hand `uploadCapture()` an
        // empty string and fail every time.
        const dataUrl = this.canvasRef.nativeElement.toDataURL('image/png');
        this.confirmedDataUrl = dataUrl;

        // `onSigned` is the script-facing hook and **nothing has ever assigned it** — nor can it.
        // Script-declared functions do not survive `ctx.dump()` at the QuickJS boundary, the same
        // constraint that made `<Button>`'s `click` inert (#24) and `InventoryEditor`'s
        // `onScopeConfirmed` inert (#15). Kept only so a script that does set it still works.
        if (typeof this.node.props?.onSigned === 'function') {
            this.node.props.onSigned(dataUrl);
        }

        // Until now that callback was the *only* thing done with the signature, so it was drawn,
        // used to satisfy the gate, and discarded when the step unmounted — #43 one field over.
        // It is now stored exactly like a step photo and attached to the same system comment.
        this._store(dataUrl);

        this._notifyReady(true);
    }

    private _store(dataUrl: string) {
        const key: string = this.node.props?.key ?? 'customer_signature';

        this.uploadState = 'uploading';

        uploadCapture(this.artifactService, dataUrl, `customer-signature-${key}.png`)
            .subscribe((artifactId) => {
                this.uploadState = artifactId ? 'stored' : 'failed';
                if (!artifactId) { return; }

                persistStepCapture(
                    { store: this.store, route: this.route },
                    key,
                    { kind: 'signature', artifactIds: [ artifactId ] },
                );
            });
    }

    retryStore() {
        if (!this.confirmed || this.uploadState === 'uploading' || !this.confirmedDataUrl) { return; }
        this._store(this.confirmedDataUrl);
    }

    reset() {
        this.confirmed = false;
        this.confirmedDataUrl = '';
        this.uploadState = 'idle';
        this.hasSig = false;
        this.signedAt = '';
        this._notifyReady(false);
        setTimeout(() => {
            // Re-init canvas after view re-renders
            if (this.canvasRef) {
                this.ctx = this.canvasRef.nativeElement.getContext('2d')!;
                this.ctx.strokeStyle = '#1a1a1a';
                this.ctx.lineWidth = 2.5;
                this.ctx.lineCap = 'round';
                this.ctx.lineJoin = 'round';
            }
        }, 50);
    }

    /** Storage state of the confirmed signature. Never gates the step — see `uploadCapture`. */
    uploadState: 'idle' | 'uploading' | 'stored' | 'failed' = 'idle';

    /**
     * The confirmed signature image, kept so a failed upload can be retried.
     *
     * Not a duplicate of the canvas: the canvas is unmounted as soon as `confirmed` is set, so this
     * is the only copy that still exists at retry time.
     */
    private confirmedDataUrl = '';

    private readonly store = inject(Store);
    private readonly route = inject(ActivatedRoute);
    private readonly artifactService = inject(ArtifactService);

    /** Tell the step gate system whether this signature is satisfied */
    private _notifyReady(ready: boolean) {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(ready);
        }
    }

    /** Public getter used by step gating to check if this component is satisfied */
    get isSatisfied(): boolean {
        if (this.node.props?.enabled === false) return true;
        if (this.node.props?.optional) return true;
        return this.confirmed;
    }
}
